import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert, TextInput, Animated, Easing} from 'react-native';
import Slider from '@react-native-community/slider';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography, borderRadius} from '~constants/design';
import ModernButton from '~components/ModernButton';
import ModernCard from '~components/ModernCard/ModernCard';
import {NavigationProps} from '~types/navigation/navigation.type';
import Haptic from 'react-native-haptic-feedback';

// Wrapper for haptic feedback that works on both native and web
const triggerHaptic = (type: any) => {
  try {
    if (Haptic && typeof Haptic.trigger === 'function') {
      Haptic.trigger(type);
    }
  } catch (error) {
    // Silently fail on web or if haptic feedback is not available
  }
};
import {ModernHeader} from '~components/ModernHeader';
import {useGetPyxiSelectQuizStatus} from '~hooks/pyxiselect/useGetPyxiSelectQuizStatus';
import {useSavePyxiSelectQuizAnswer} from '~hooks/pyxiselect/useSavePyxiSelectQuizAnswer';
import {useGetPyxiSelectAllData} from '~hooks/pyxiselect/useGetPyxiSelectAllData';
import QuizSuccessModal from '~components/QuizSuccessModal';
import {SCREENS} from '~constants';
const successLottie = require('~assets/lottie/success.json');
import DateTimeModal from '~components/DateTimeModal';
import moment from 'moment-timezone';

const KEY_TO_LABEL: Record<string, string> = {
  first_name: 'Enter your First name',
  last_name: 'Enter your Last name',
  email: 'Enter your Email',
  date_of_birth: 'Enter your Date of birth',
  gender: 'Select your Gender',
  phone_number: 'Enter your Phone number',
  politics_religion_engagement: 'Do you feel strongly about politics or religion?',
  social_style: 'When you’re in a small group, how do you usually show up?',
  social_preference: 'Which setting feels most like your kind of night?',
  interests: 'Where do you feel most in your element?',
  energy_preference: 'Is there a type of energy you’d rather avoid?',
  three_words: 'How would you describe yourself in three words?',
  dinner_price_preference:
    'When it comes to a thoughtfully hosted dinner experience, what’s a price that feels comfortable for you?',
  industry: 'What industry are you in?',
  cultural_interests: 'What kind of cultural experiences excite you most?',
  cultural_curiosity: 'How would you describe your cultural curiosity?',
  networking_preference: 'Who would you love to meet at The Culture Edit?',
  social_preference_time: 'When do you usually go out for cultural experiences?',
  drink_preference: 'What would you like your first drink to be?',
  current_routine: 'What best describes your current routine?',
  availability: 'When are you usually free for activities?',
  activity_interests: 'What kind of activities are you most interested in?',
  event_types_preference: 'What types of events do you enjoy?',
  people_preference: 'Who would you like to meet through Pyxi?',
  frequency_preference: 'How often would you like to be matched or invited to events?',
  dietary_restrictions: 'Do you have any dietary restrictions?',
  dietary_requirements: 'Do you have any dietary requirements?',
  country_of_origin: 'What\'s your country of origin?',
  openness_activities: 'Would you be open to:',
  postcode: 'What is your postcode?',
  work_postcode: 'What is the postcode of your place of work?',
  local_business_recommendations: 'Know a local cafe, pub, or business we should invite to join Pyxi?',
  additional_notes: 'Anything else you\'d like us to know?',
};

type QuestionDef = {
  key: string;
  label: string;
  type: 'text' | 'multiline' | 'select' | 'date' | 'phone' | 'chips' | 'scale' | 'multiselect';
  maxSelect?: number; // only for multiselect
  placeholder?: string;
  options?: string[];
  searchable?: boolean; // for searchable select fields
  showSelectedAtTop?: boolean; // for showing selected option at top
};

// Field mapping by form type
const FORM_TYPE_FIELDS: Record<string, string[]> = {
  user: ['first_name', 'last_name', 'email', 'phone_number', 'date_of_birth', 'gender'],
  general: ['industry', 'energy_preference', 'three_words'],
  reset_table: ['social_style', 'social_preference', 'interests', 'politics_religion_engagement', 'dietary_requirements', 'country_of_origin', 'dinner_price_preference'],
  cultural_edit: ['cultural_interests', 'cultural_curiosity', 'networking_preference', 'social_preference_time'],
  neighborhood: ['first_name', 'last_name', 'phone_number', 'email', 'date_of_birth', 'gender', 'industry', 'energy_preference', 'three_words', 'current_routine', 'availability', 'activity_interests', 'event_types_preference', 'people_preference', 'frequency_preference', 'dietary_restrictions', 'dietary_requirements', 'openness_activities', 'postcode', 'work_postcode', 'local_business_recommendations', 'additional_notes'],
  housewarming: ['social_style', 'social_preference', 'interests', 'drink_preference'], // Housewarming specific fields based on API response
};

const FIELD_CONFIG: Partial<Record<string, QuestionDef>> = {
  first_name: {key: 'first_name', label: KEY_TO_LABEL.first_name, type: 'text', placeholder: 'Enter your first name'},
  last_name: {key: 'last_name', label: KEY_TO_LABEL.last_name, type: 'text', placeholder: 'Enter your last name'},
  email: {key: 'email', label: KEY_TO_LABEL.email, type: 'text', placeholder: 'Enter your email'},
  phone_number: {
    key: 'phone_number',
    label: KEY_TO_LABEL.phone_number,
    type: 'phone',
    placeholder: 'Enter your phone number',
  },
  date_of_birth: {key: 'date_of_birth', label: KEY_TO_LABEL.date_of_birth, type: 'date', placeholder: 'YYYY-MM-DD'},
  gender: {
    key: 'gender',
    label: KEY_TO_LABEL.gender,
    type: 'select',
    options: ['Man', 'Woman', 'Non-binary', 'Prefer not to say'],
  },
  politics_religion_engagement: {
    key: 'politics_religion_engagement',
    label: KEY_TO_LABEL.politics_religion_engagement,
    type: 'select',
    options: ['Political', 'Religious', 'Both', 'Not really / Not at all'],
  },
  social_style: {
    key: 'social_style',
    label: KEY_TO_LABEL.social_style,
    type: 'select',
    options: ['I tend to observe and listen', 'I warm up after a little while', `I'm naturally chatty and outgoing​`],
  },
  social_preference: {
    key: 'social_preference',
    label: KEY_TO_LABEL.social_preference,
    type: 'multiselect',
    maxSelect: 2,
    options: [
      'Talk philosophy over wine',
      'Laugh over spicy food',
      'Share playlists in silence',
      'Try something weird and new​',
    ],
  },
  interests: {
    key: 'interests',
    label: KEY_TO_LABEL.interests,
    type: 'multiselect',
    maxSelect: 2,
    options: [
      'Taking in culture — exhibitions, film, theatre',
      'Getting hands-on — workshops, making things',
      'Keeping it light — dinners, drinks, live music',
      'Moving around — nature, walks, being active',
    ],
  },
  energy_preference: {
    key: 'energy_preference',
    label: KEY_TO_LABEL.energy_preference,
    type: 'text',
    placeholder: 'E.g., too loud, too quiet',
  },
  cultural_interests: {
    key: 'cultural_interests',
    label: KEY_TO_LABEL.cultural_interests,
    type: 'multiselect',
    maxSelect: 2,
    options: [
      '🎨 Art & Design',
      '🎭 Theatre & Performance',
      '🎶 Music & Live Events',
      '📚 Literature & Talks',
      '🍷 Food & Wine Experiences',
      '🌍 Global Culture & Travel',
    ],
  },
  cultural_curiosity: {
    key: 'cultural_curiosity',
    label: KEY_TO_LABEL.cultural_curiosity,
    type: 'select',
    options: [
      'I’m exploring casually — love discovering new things',
      'I’m deeply passionate — culture is my lifestyle',
      `I’m somewhere in between​`,
    ],
  },
  networking_preference: {
    key: 'networking_preference',
    label: KEY_TO_LABEL.networking_preference,
    type: 'select',
    options: ['People with similar knowledge and interests', 'People from all walks of life', 'Surprise me!', 'Other'],
  },
  social_preference_time: {
    key: 'social_preference_time',
    label: KEY_TO_LABEL.social_preference_time,
    type: 'select',
    options: ['Weeknights', 'Weekends', 'Either works'],
  },
  three_words: {
    key: 'three_words',
    label: KEY_TO_LABEL.three_words,
    type: 'multiselect',
    maxSelect: 3,
    options: [
      'Outgoing',
      'Creative',
      'Curious',
      'Calm',
      'Playful',
      'Analytical',
      'Adventurous',
      'Empathetic',
      'Witty',
      'Driven',
      'Laid-back',
      'Talkative',
      'Quiet',
      'Open-minded',
      'Spontaneous',
    ],
  },
  dinner_price_preference: {
    key: 'dinner_price_preference',
    label: KEY_TO_LABEL.dinner_price_preference,
    type: 'select',
    options: ['£', '££', '£££'],
  },
  industry: {key: 'industry', label: KEY_TO_LABEL.industry, type: 'text', placeholder: 'Your industry/field'},
  drink_preference: {
    key: 'drink_preference',
    label: KEY_TO_LABEL.drink_preference,
    type: 'select',
    options: [
      'Alcoholic',
      'Non-alcoholic',
    ],
  },
  current_routine: {
    key: 'current_routine',
    label: KEY_TO_LABEL.current_routine,
    type: 'multiselect',
    maxSelect: 5,
    options: ['Remote worker', 'Office-based', 'Freelancer', 'Parent/carer', 'Retired', 'Student', 'Shift worker', 'Looking for work'],
  },
  availability: {
    key: 'availability',
    label: KEY_TO_LABEL.availability,
    type: 'multiselect',
    maxSelect: 5,
    options: ['Weekday mornings', 'Weekday afternoons', 'Weekday evenings', 'Weekends only', 'Varies week to week'],
  },
  activity_interests: {
    key: 'activity_interests',
    label: KEY_TO_LABEL.activity_interests,
    type: 'multiselect',
    maxSelect: 8,
    options: ['Coffee meet ups', 'Walks / Dog walking', 'Fitness (yoga, running, etc)', 'Family-friendly events', 'Book or film clubs', 'Dinner / Brunch outings', 'Pub quizzes or games', 'Creative or artsy sessions', 'Local causes / volunteering', 'Networking'],
  },
  event_types_preference: {
    key: 'event_types_preference',
    label: KEY_TO_LABEL.event_types_preference,
    type: 'multiselect',
    maxSelect: 5,
    options: ['One-on-one meetups', 'Small groups (3-6 people)', 'Large gatherings', 'Outdoor or active', 'Structured (e.g. set dinners, hosted)'],
  },
  people_preference: {
    key: 'people_preference',
    label: KEY_TO_LABEL.people_preference,
    type: 'multiselect',
    maxSelect: 6,
    options: ['Other parents', 'Dog owners', 'Similar age', 'Similar interests', 'Fellow remote workers', 'People with different backgrounds', 'I\'m open to anyone nearby', 'Other'],
  },
  frequency_preference: {
    key: 'frequency_preference',
    label: KEY_TO_LABEL.frequency_preference,
    type: 'select',
    options: ['Every week', 'A couple of times per month', 'Occasionally', 'Just for special events'],
  },
  dietary_restrictions: {
    key: 'dietary_restrictions',
    label: KEY_TO_LABEL.dietary_restrictions,
    type: 'multiselect',
    maxSelect: 3,
    options: ['No restrictions', 'Vegetarian', 'Vegan', 'Other'],
  },
  dietary_requirements: {
    key: 'dietary_requirements',
    label: KEY_TO_LABEL.dietary_requirements,
    type: 'select',
    options: ['Pescatarian', 'Vegetarian', 'Vegan', 'Other'],
  },
  country_of_origin: {
    key: 'country_of_origin',
    label: KEY_TO_LABEL.country_of_origin,
    type: 'select',
    options: [
      '🇦🇫 Afghanistan',
      '🇦🇱 Albania',
      '🇩🇿 Algeria',
      '🇦🇩 Andorra',
      '🇦🇴 Angola',
      '🇦🇬 Antigua and Barbuda',
      '🇦🇷 Argentina',
      '🇦🇲 Armenia',
      '🇦🇺 Australia',
      '🇦🇹 Austria',
      '🇦🇿 Azerbaijan',
      '🇧🇸 Bahamas',
      '🇧🇭 Bahrain',
      '🇧🇩 Bangladesh',
      '🇧🇧 Barbados',
      '🇧🇾 Belarus',
      '🇧🇪 Belgium',
      '🇧🇿 Belize',
      '🇧🇯 Benin',
      '🇧🇹 Bhutan',
      '🇧🇴 Bolivia',
      '🇧🇦 Bosnia and Herzegovina',
      '🇧🇼 Botswana',
      '🇧🇷 Brazil',
      '🇧🇳 Brunei',
      '🇧🇬 Bulgaria',
      '🇧🇫 Burkina Faso',
      '🇧🇮 Burundi',
      '🇨🇻 Cape Verde',
      '🇰🇭 Cambodia',
      '🇨🇲 Cameroon',
      '🇨🇦 Canada',
      '🇨🇫 Central African Republic',
      '🇹🇩 Chad',
      '🇨🇱 Chile',
      '🇨🇳 China',
      '🇨🇴 Colombia',
      '🇰🇲 Comoros',
      '🇨🇬 Congo',
      '🇨🇷 Costa Rica',
      '🇭🇷 Croatia',
      '🇨🇺 Cuba',
      '🇨🇾 Cyprus',
      '🇨🇿 Czech Republic',
      '🇩🇰 Denmark',
      '🇩🇯 Djibouti',
      '🇩🇲 Dominica',
      '🇩🇴 Dominican Republic',
      '🇪🇨 Ecuador',
      '🇪🇬 Egypt',
      '🇸🇻 El Salvador',
      '🇬🇶 Equatorial Guinea',
      '🇪🇷 Eritrea',
      '🇪🇪 Estonia',
      '🇪🇹 Ethiopia',
      '🇫🇯 Fiji',
      '🇫🇮 Finland',
      '🇫🇷 France',
      '🇬🇦 Gabon',
      '🇬🇲 Gambia',
      '🇬🇪 Georgia',
      '🇩🇪 Germany',
      '🇬🇭 Ghana',
      '🇬🇷 Greece',
      '🇬🇩 Grenada',
      '🇬🇹 Guatemala',
      '🇬🇳 Guinea',
      '🇬🇼 Guinea-Bissau',
      '🇬🇾 Guyana',
      '🇭🇹 Haiti',
      '🇭🇳 Honduras',
      '🇭🇺 Hungary',
      '🇮🇸 Iceland',
      '🇮🇳 India',
      '🇮🇩 Indonesia',
      '🇮🇷 Iran',
      '🇮🇶 Iraq',
      '🇮🇪 Ireland',
      '🇮🇱 Israel',
      '🇮🇹 Italy',
      '🇯🇲 Jamaica',
      '🇯🇵 Japan',
      '🇯🇴 Jordan',
      '🇰🇿 Kazakhstan',
      '🇰🇪 Kenya',
      '🇰🇮 Kiribati',
      '🇽🇰 Kosovo',
      '🇰🇼 Kuwait',
      '🇰🇬 Kyrgyzstan',
      '🇱🇦 Laos',
      '🇱🇻 Latvia',
      '🇱🇧 Lebanon',
      '🇱🇸 Lesotho',
      '🇱🇷 Liberia',
      '🇱🇾 Libya',
      '🇱🇮 Liechtenstein',
      '🇱🇹 Lithuania',
      '🇱🇺 Luxembourg',
      '🇲🇬 Madagascar',
      '🇲🇼 Malawi',
      '🇲🇾 Malaysia',
      '🇲🇻 Maldives',
      '🇲🇱 Mali',
      '🇲🇹 Malta',
      '🇲🇭 Marshall Islands',
      '🇲🇷 Mauritania',
      '🇲🇺 Mauritius',
      '🇲🇽 Mexico',
      '🇫🇲 Micronesia',
      '🇲🇩 Moldova',
      '🇲🇨 Monaco',
      '🇲🇳 Mongolia',
      '🇲🇪 Montenegro',
      '🇲🇦 Morocco',
      '🇲🇿 Mozambique',
      '🇲🇲 Myanmar',
      '🇳🇦 Namibia',
      '🇳🇷 Nauru',
      '🇳🇵 Nepal',
      '🇳🇱 Netherlands',
      '🇳🇿 New Zealand',
      '🇳🇮 Nicaragua',
      '🇳🇪 Niger',
      '🇳🇬 Nigeria',
      '🇲🇰 North Macedonia',
      '🇳🇴 Norway',
      '🇴🇲 Oman',
      '🇵🇰 Pakistan',
      '🇵🇼 Palau',
      '🇵🇦 Panama',
      '🇵🇬 Papua New Guinea',
      '🇵🇾 Paraguay',
      '🇵🇪 Peru',
      '🇵🇭 Philippines',
      '🇵🇱 Poland',
      '🇵🇹 Portugal',
      '🇶🇦 Qatar',
      '🇷🇴 Romania',
      '🇷🇺 Russia',
      '🇷🇼 Rwanda',
      '🇰🇳 Saint Kitts and Nevis',
      '🇱🇨 Saint Lucia',
      '🇻🇨 Saint Vincent and the Grenadines',
      '🇼🇸 Samoa',
      '🇸🇲 San Marino',
      '🇸🇹 Sao Tome and Principe',
      '🇸🇦 Saudi Arabia',
      '🇸🇳 Senegal',
      '🇷🇸 Serbia',
      '🇸🇨 Seychelles',
      '🇸🇱 Sierra Leone',
      '🇸🇬 Singapore',
      '🇸🇰 Slovakia',
      '🇸🇮 Slovenia',
      '🇸🇧 Solomon Islands',
      '🇸🇴 Somalia',
      '🇿🇦 South Africa',
      '🇰🇷 South Korea',
      '🇸🇸 South Sudan',
      '🇪🇸 Spain',
      '🇱🇰 Sri Lanka',
      '🇸🇩 Sudan',
      '🇸🇷 Suriname',
      '🇸🇪 Sweden',
      '🇨🇭 Switzerland',
      '🇸🇾 Syria',
      '🇹🇼 Taiwan',
      '🇹🇯 Tajikistan',
      '🇹🇿 Tanzania',
      '🇹🇭 Thailand',
      '🇹🇱 Timor-Leste',
      '🇹🇬 Togo',
      '🇹🇴 Tonga',
      '🇹🇹 Trinidad and Tobago',
      '🇹🇳 Tunisia',
      '🇹🇷 Turkey',
      '🇹🇲 Turkmenistan',
      '🇹🇻 Tuvalu',
      '🇺🇬 Uganda',
      '🇺🇦 Ukraine',
      '🇦🇪 United Arab Emirates',
      '🇬🇧 United Kingdom',
      '🇺🇸 United States',
      '🇺🇾 Uruguay',
      '🇺🇿 Uzbekistan',
      '🇻🇺 Vanuatu',
      '🇻🇦 Vatican City',
      '🇻🇪 Venezuela',
      '🇻🇳 Vietnam',
      '🇾🇪 Yemen',
      '🇿🇲 Zambia',
      '🇿🇼 Zimbabwe',
    ],
    searchable: true,
    showSelectedAtTop: true,
  },
  openness_activities: {
    key: 'openness_activities',
    label: KEY_TO_LABEL.openness_activities,
    type: 'multiselect',
    maxSelect: 3,
    options: ['Hosting an event', 'Volunteering with the Pyxi team', 'Not at the moment'],
  },
  postcode: {
    key: 'postcode',
    label: KEY_TO_LABEL.postcode,
    type: 'text',
    placeholder: 'Enter your postcode',
  },
  work_postcode: {
    key: 'work_postcode',
    label: KEY_TO_LABEL.work_postcode,
    type: 'text',
    placeholder: 'Enter your work postcode',
  },
  local_business_recommendations: {
    key: 'local_business_recommendations',
    label: KEY_TO_LABEL.local_business_recommendations,
    type: 'multiline',
    placeholder: 'Tell us about local businesses you recommend',
  },
  additional_notes: {
    key: 'additional_notes',
    label: KEY_TO_LABEL.additional_notes,
    type: 'multiline',
    placeholder: 'Share any additional information',
  },
};

// Mapping for gender field: API values to UI display values
const GENDER_API_TO_UI: Record<string, string> = {
  man: 'Man',
  woman: 'Woman',
  other: 'Non-binary',
  prefer_not_to_say: 'Prefer not to say',
};

// Reverse mapping: UI display values to API values
const GENDER_UI_TO_API: Record<string, string> = {
  'Man': 'man',
  'Woman': 'woman',
  'Non-binary': 'other',
  'Prefer not to say': 'prefer_not_to_say',
};

const PyxiSelectQuiz: React.FC = () => {
  const {colors} = useTheme();
  const navigation = useNavigation<NavigationProps>();
  const route = useRoute<any>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const progressAnim = useRef(new Animated.Value(0)).current;
  const hasPopulatedAnswers = useRef(false);
  const [quizSuccessVisible, setQuizSuccessVisible] = useState(false);
  // Local state for date picker
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(new Date());
  // Local state for search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const formType = route.params?.form_type || 'reset_table';
  const mode = route.params?.mode || 'edit'; // 'edit', 'view', 'retake'
  const passedFieldData = route.params?.fieldData;
  const passedStatusData = route.params?.statusData;
  
  // Only make API calls if data is not passed from navigation
  const shouldFetchStatus = !passedStatusData;
  const shouldFetchAllData = (mode === 'view' || mode === 'retake') && !passedFieldData;
  
  const {data, isLoading, error, refetch} = useGetPyxiSelectQuizStatus(shouldFetchStatus, formType);
  const {data: allData, isLoading: isAllDataLoading} = useGetPyxiSelectAllData(shouldFetchAllData);
  const {mutateAsync, isLoading: isSaving} = useSavePyxiSelectQuizAnswer();
  
  // Use passed data if available, otherwise use API data
  const status = passedStatusData || data;
  const currentAllData = passedFieldData || allData;
  const loading = (shouldFetchStatus && isLoading) || (shouldFetchAllData && isAllDataLoading);

  const notFilled = status?.not_filled ?? [];
  const filled = status?.filled ?? [];
  
  // Helper function to get relevant fields for the current form type
  const getRelevantFields = useCallback((fields: string[], currentFormType: string) => {
    if (currentFormType === 'user') {
      // For 'user' form type, include user, general, and reset_table fields
      const relevantFields = [
        ...FORM_TYPE_FIELDS.user,
        ...FORM_TYPE_FIELDS.general,
        ...FORM_TYPE_FIELDS.reset_table,
      ];
      return fields.filter(field => relevantFields.includes(field));
    } else if (currentFormType === 'cultural_edit') {
      // For 'cultural_edit', only show cultural_edit fields
      return fields.filter(field => FORM_TYPE_FIELDS.cultural_edit.includes(field));
    } else if (currentFormType === 'neighborhood') {
      // For 'neighborhood', only show neighborhood fields
      return fields.filter(field => FORM_TYPE_FIELDS.neighborhood.includes(field));
    } else if (currentFormType === 'housewarming') {
      // For 'housewarming', include user, general, and housewarming fields
      const relevantFields = [
        ...FORM_TYPE_FIELDS.user,
        ...FORM_TYPE_FIELDS.general,
        ...FORM_TYPE_FIELDS.housewarming,
      ];
      return fields.filter(field => relevantFields.includes(field));
    }
    // Default: return all fields
    return fields;
  }, []);

  // Filter fields based on the current form type
  const filteredNotFilled = useMemo(() => getRelevantFields(notFilled, formType), [notFilled, formType, getRelevantFields]);
  const filteredFilled = useMemo(() => getRelevantFields(filled, formType), [filled, formType, getRelevantFields]);
  
  // In retake mode, show all fields (both filled and not filled) except email
  // In view mode, show all fields but make them read-only
  // In edit mode (default), show only not filled fields
  const allFields = useMemo(() => [...filteredFilled, ...filteredNotFilled], [filteredFilled, filteredNotFilled]);
  const fieldsToShow = useMemo(() => {
    const fields = mode === 'retake' || mode === 'view' ? allFields : filteredNotFilled;
    // Exclude email field in retake mode
    return mode === 'retake' ? fields.filter(field => field !== 'email') : fields;
  }, [mode, allFields, filteredNotFilled]);

  useFocusEffect(
    useCallback(() => {
      // Only refetch if we're not using passed data
      if (shouldFetchStatus) {
        refetch();
      }
    }, [refetch, shouldFetchStatus]),
  );

  // Reset population flag when mode, formType, or passed data changes
  useEffect(() => {
    hasPopulatedAnswers.current = false;
    setAnswers({});
  }, [mode, formType, passedFieldData, passedStatusData]);

  // Handle answer population based on mode
  useEffect(() => {
    if (!loading && !hasPopulatedAnswers.current) {
      if (mode === 'view' && currentAllData && status) {
        // View mode: Pre-fill with existing data
        const existingAnswers: Record<string, string> = {};
        
        // Apply the same field filtering logic as used in the main component
        const getRelevantFieldsLocal = (fields: string[], currentFormType: string) => {
          if (currentFormType === 'user') {
            // For 'user' form type, include user, general, and reset_table fields
            const relevantFields = [
              ...FORM_TYPE_FIELDS.user,
              ...FORM_TYPE_FIELDS.general,
              ...FORM_TYPE_FIELDS.reset_table,
            ];
            return fields.filter(field => relevantFields.includes(field));
          } else if (currentFormType === 'cultural_edit') {
            // For 'cultural_edit', only show cultural_edit fields
            return fields.filter(field => FORM_TYPE_FIELDS.cultural_edit.includes(field));
          } else if (currentFormType === 'neighborhood') {
            // For 'neighborhood', only show neighborhood fields
            return fields.filter(field => FORM_TYPE_FIELDS.neighborhood.includes(field));
          } else if (currentFormType === 'housewarming') {
            // For 'housewarming', include user, general, and housewarming fields
            const relevantFields = [
              ...FORM_TYPE_FIELDS.user,
              ...FORM_TYPE_FIELDS.general,
              ...FORM_TYPE_FIELDS.housewarming,
            ];
            return fields.filter(field => relevantFields.includes(field));
          }
          // Default: return all fields
          return fields;
        };

        const currentFilled = status.filled ?? [];
        const currentNotFilled = status.not_filled ?? [];
        const filteredCurrentFilled = getRelevantFieldsLocal(currentFilled, formType);
        const filteredCurrentNotFilled = getRelevantFieldsLocal(currentNotFilled, formType);
        const currentAllFields = [...filteredCurrentFilled, ...filteredCurrentNotFilled];
        
        // Get data source - either passed fieldData (flat structure) or API allData (nested structure)
        let sectionData: any;
        if (passedFieldData) {
          // Use passed field data (already combined/filtered by SettingsView)
          sectionData = passedFieldData;
        } else {
          // Use API data with original nested structure
          const getDataFromSection = (sectionName: string) => {
            if (!currentAllData) return {};
            switch (sectionName) {
              case 'user':
                return currentAllData.user.data;
              case 'cultural_edit':
                return currentAllData.cultural_edit.data;
              case 'neighborhood':
                return currentAllData.neighborhood.data;
              case 'housewarming':
                return currentAllData.housewarming.data;
              default:
                // For 'user' formType, also include general and reset_table data
                return {
                  ...currentAllData.user.data,
                  ...currentAllData.general.data,
                  ...currentAllData.reset_table.data,
                };
            }
          };
          sectionData = getDataFromSection(formType);
        }
        
        // Populate answers from data
        currentAllFields.forEach(fieldKey => {
          const dataValue = (sectionData as any)[fieldKey];
          if (dataValue !== undefined && dataValue !== null) {
            let value = dataValue;
            
            // Handle multiselect fields - convert comma-separated string back to JSON array
            const fieldConfig = FIELD_CONFIG[fieldKey];
            if (fieldConfig?.type === 'multiselect' && typeof value === 'string') {
              try {
                // Instead of splitting by comma (which fails when options contain commas),
                // check which options are included in the value string
                const options = fieldConfig.options || [];
                const arrayValue = options.filter(option => value.includes(option));
                value = JSON.stringify(arrayValue);
              } catch {
                // Keep original value if conversion fails
              }
            }
            
            // Map gender API values to UI display values
            if (fieldKey === 'gender' && typeof value === 'string') {
              value = GENDER_API_TO_UI[value] || value;
            }
            
            existingAnswers[fieldKey] = String(value);
          }
        });
        
        setAnswers(existingAnswers);
      } else if (mode === 'retake') {
        // Retake mode: Start with empty answers
        setAnswers({});
      }
      
      hasPopulatedAnswers.current = true;
    }
  }, [mode, currentAllData, formType, status, loading, passedFieldData]);

  const hasNothingToFill = !loading && !error && fieldsToShow.length === 0;

  // Choose subtitle text based on form type and mode
  const subtitleText = useMemo(() => {
    const ft = String(formType || '').toLowerCase();
    const isCultureEdit = ft === 'cultural_edit' || ft.includes('cultural');
    const isHousewarming = ft === 'housewarming';
    
    if (mode === 'view') {
      return 'Review your current answers below.';
    } else if (mode === 'retake') {
      if (isCultureEdit) {
        return 'Update your answers to improve your group placement.';
      } else if (isHousewarming) {
        return 'Update your answers to improve your housewarming event matching.';
      } else {
        return 'Update your answers to improve your dinner matching.';
      }
    } else {
      if (isCultureEdit) {
        return 'We need a few more details to place you in the best group.';
      } else if (isHousewarming) {
        return 'We need a few more details to match you with the perfect housewarming experience.';
      } else {
        return 'We need a few more details to place you in the best dinner.';
      }
    }
  }, [formType, mode]);

  const titleText = useMemo(() => {
    if (mode === 'view') {
      return 'Your Pyxi Select Profile';
    } else if (mode === 'retake') {
      return 'Retake Pyxi Select Quiz';
    } else {
      return 'Complete your Pyxi Select profile';
    }
  }, [mode]);

  const questions: QuestionDef[] = useMemo(() => {
    return fieldsToShow.map(key => {
      const config = FIELD_CONFIG[key] || {key, label: KEY_TO_LABEL[key] || key, type: 'text'};
      
      // For housewarming events, add "Dance all night long" option to social_preference
      if (key === 'social_preference' && formType === 'housewarming') {
        return {
          ...config,
          options: [
            ...(config.options || []),
            'Dance all night long',
          ],
        };
      }
      
      return config;
    });
  }, [fieldsToShow, formType]);

  const current = questions[currentIndex];
  const totalQuestions = questions.length || 1;
  const progressRatio = Math.min(1, Math.max(0, (currentIndex + 1) / totalQuestions));

  const isCurrentAnswered = useMemo(() => {
    if (!current) return false;
    const val = answers[current.key];
    if (current.type === 'multiselect') {
      try {
        const parsed = val ? JSON.parse(val) : [];
        if (!Array.isArray(parsed) || parsed.length === 0) return false;
        const opts = current.options || [];
        // If 'Other' is selected but no custom text provided, not answered yet
        const hasOther = parsed.includes('Other');
        const hasCustom = parsed.some(v => !opts.includes(v));
        if (hasOther && !hasCustom) return false;
        return true;
      } catch {
        return false;
      }
    }
    if (current.type === 'scale') {
      return true;
    }
    if (current.type === 'select') {
      const opts = current.options || [];
      if (!val || val.trim().length === 0) return false;
      if (opts.includes('Other')) {
        if (val === 'Other') return false; // must specify text
        // If value is a custom text not in options, accept
        return true;
      }
      return opts.length ? opts.includes(val) : val.trim().length > 0;
    }
    return typeof val === 'string' && val.trim().length > 0;
  }, [answers, current]);

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: progressRatio,
      duration: 350,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [progressRatio, progressAnim]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {flex: 1, backgroundColor: colors.background},
        content: {padding: spacing.lg},
        title: {
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.textPrimary,
          marginBottom: spacing.sm,
        },
        subtitle: {
          fontSize: typography.fontSize.base,
          color: colors.textSecondary,
          marginBottom: spacing.lg,
        },
        questionTitle: {
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textPrimary,
          marginBottom: spacing.sm,
        },
        sectionTitle: {
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textPrimary,
          marginBottom: spacing.sm,
        },
        chip: {
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: borderRadius.full,
          borderWidth: 1,
          borderColor: colors.border + '40',
          backgroundColor: colors.surface,
          marginRight: spacing.sm,
          marginBottom: spacing.sm,
        },
        chipText: {
          color: colors.textPrimary,
          fontSize: typography.fontSize.sm,
        },
        chipSelected: {
          borderColor: colors.primary,
          backgroundColor: colors.primary + '10',
        },
        chipsRow: {flexDirection: 'row', flexWrap: 'wrap'},
        card: {marginBottom: spacing.lg},
        option: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: spacing.md + 2,
          paddingHorizontal: spacing.lg,
          marginBottom: spacing.md,
          borderRadius: spacing.lg,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border + '40',
          shadowColor: '#000',
          shadowOpacity: 0.06,
          shadowOffset: {width: 0, height: 4},
          shadowRadius: 8,
          elevation: 1,
        },
        optionSelected: {
          borderColor: colors.primary,
          backgroundColor: colors.primary + '10',
        },
        radioOuter: {
          height: 24,
          width: 24,
          borderRadius: 12,
          borderWidth: 2,
          borderColor: colors.border,
          alignItems: 'center',
          justifyContent: 'center',
        },
        radioInner: {
          width: 12,
          height: 12,
          borderRadius: 6,
          backgroundColor: colors.primary,
        },
        // Checkbox for multiselect
        checkboxOuter: {
          height: 22,
          width: 22,
          borderRadius: 6,
          borderWidth: 2,
          borderColor: colors.border,
          alignItems: 'center',
          justifyContent: 'center',
        },
        checkboxInner: {
          width: 12,
          height: 12,
          borderRadius: 3,
          backgroundColor: colors.primary,
        },
        optionText: {
          marginLeft: spacing.md,
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          color: colors.textPrimary,
          flex: 1,
        },
        // Scale (0-10) slider styles
        scaleInfo: {
          marginTop: spacing.xs,
          marginBottom: spacing.sm,
          color: colors.textSecondary,
        },
        sliderWrap: {
          marginTop: spacing.md,
          marginBottom: spacing.sm,
        },
        sliderValue: {
          textAlign: 'center',
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.primary,
        },
        // Phone input with +44 prefix
        phoneRow: {
          flexDirection: 'row',
          alignItems: 'center',
          borderWidth: 1,
          borderColor: colors.border,
          borderRadius: spacing.lg,
          backgroundColor: colors.surface,
          marginTop: spacing.md,
          overflow: 'hidden',
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowOffset: {width: 0, height: 3},
          shadowRadius: 8,
          elevation: 1,
        },
        countryBox: {
          paddingHorizontal: spacing.md,
          paddingVertical: spacing.md,
          backgroundColor: colors.background,
          borderRightWidth: 1,
          borderRightColor: colors.border + '60',
        },
        countryText: {
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textPrimary,
        },
        phoneInput: {
          flex: 1,
          paddingVertical: spacing.md,
          paddingHorizontal: spacing.md,
          fontSize: typography.fontSize.base,
          color: colors.textPrimary,
        },
        input: {
          padding: spacing.md + 2,
          borderWidth: 1,
          borderColor: colors.border,
          borderRadius: spacing.lg,
          fontSize: typography.fontSize.base,
          color: colors.textPrimary,
          backgroundColor: colors.surface,
          marginTop: spacing.md,
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowOffset: {width: 0, height: 3},
          shadowRadius: 8,
          elevation: 1,
        },
        inputButton: {
          padding: spacing.md + 2,
          borderWidth: 1,
          borderColor: colors.border,
          borderRadius: spacing.lg,
          backgroundColor: colors.surface,
          marginTop: spacing.md,
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowOffset: {width: 0, height: 3},
          shadowRadius: 8,
          elevation: 1,
        },
        inputButtonText: {
          fontSize: typography.fontSize.base,
          color: colors.textPrimary,
        },
        footer: {
          paddingHorizontal: spacing.lg,
          paddingBottom: spacing.lg,
          paddingTop: spacing.md,
          borderTopWidth: 1,
          borderTopColor: colors.border + '30',
          backgroundColor: colors.background,
        },
        progress: {
          textAlign: 'center',
          color: colors.textSecondary,
          marginBottom: spacing.sm,
        },
        progressTrack: {
          height: 8,
          borderRadius: 4,
          backgroundColor: colors.border + '30',
          overflow: 'hidden',
        },
        progressFill: {
          height: '100%',
          backgroundColor: colors.primary,
          borderRadius: 4,
          width: '0%',
        },
        heroRow: {
          marginBottom: spacing.sm,
        },
        buttonRow: {flexDirection: 'row', gap: spacing.md},
        buttonFlex: {flex: 1},
        // Search input styles
        searchInput: {
          padding: spacing.md + 2,
          borderWidth: 1,
          borderColor: colors.border,
          borderRadius: spacing.lg,
          fontSize: typography.fontSize.base,
          color: colors.textPrimary,
          backgroundColor: colors.surface,
          marginTop: spacing.md,
          marginBottom: spacing.sm,
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowOffset: {width: 0, height: 3},
          shadowRadius: 8,
          elevation: 1,
        },
        selectedCountryContainer: {
          padding: spacing.md,
          backgroundColor: colors.primary + '10',
          borderRadius: spacing.lg,
          borderWidth: 1,
          borderColor: colors.primary,
          marginBottom: spacing.sm,
        },
        selectedCountryText: {
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          color: colors.primary,
        },
      }),
    [colors],
  );

  const goBackHandler = () => {
    navigation.goBack();
  };

  const handleContinuePress = async () => {
    if (!current) return;
    let currentAnswer = answers[current.key];

    if (current.type === 'multiselect') {
      let count = 0;
      try {
        const parsed = currentAnswer ? JSON.parse(currentAnswer) : [];
        count = Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        count = 0;
      }
      if (count === 0) {
        Alert.alert('Answer required', 'Please select at least one option to continue.');
        return;
      }
      // If 'Other' selected, ensure text provided
      try {
        const parsed = currentAnswer ? JSON.parse(currentAnswer) : [];
        if (Array.isArray(parsed)) {
          const opts = current.options || [];
          const hasOther = parsed.includes('Other');
          const hasCustom = parsed.some(v => !opts.includes(v));
          if (hasOther && !hasCustom) {
            Alert.alert('More detail needed', 'Please specify your "Other" answer.');
            return;
          }
        }
      } catch {}
    } else if (current.type === 'scale') {
      // Default to 0 if user didn't touch the slider
      if (!currentAnswer || currentAnswer.trim().length === 0) {
        currentAnswer = '0';
        setAnswers(prev => ({...prev, [current.key]: '0'}));
      }
    } else if (current.type === 'select' && (current.options || []).includes('Other')) {
      if (currentAnswer === 'Other') {
        Alert.alert('More detail needed', 'Please specify your "Other" answer.');
        return;
      }
    } else if (!currentAnswer || currentAnswer.trim().length === 0) {
      Alert.alert('Answer required', 'Please provide an answer to continue.');
      return;
    }

    // Call save API before advancing
    if (submitting) return;
    setSubmitting(true);
    try {
      console.log('api start');

      // Convert multiselect from JSON array -> comma-separated string as required by API
      let valueToSend = currentAnswer;
      if (current.type === 'multiselect') {
        try {
          const parsed = currentAnswer ? JSON.parse(currentAnswer) : [];
          if (Array.isArray(parsed)) {
            valueToSend = parsed.join(', ');
          }
        } catch {}
      }
      
      await mutateAsync({
        form_type: formType,
        field: current.key,
        value: valueToSend,
      });
      triggerHaptic('impactLight');
      if (currentIndex === questions.length - 1) {
        // If we were navigated here from an event, show a success modal then redirect to buy ticket
        if (route.params?.fromEvent && (route.params?.event || route.params?.selectedRecurrenceEventId)) {
          try {
            // Show success modal and then navigate to buy ticket
            const targetEventId = route.params?.selectedRecurrenceEventId || route.params?.event?.event_id;
            setQuizSuccessVisible(true);
            setTimeout(() => {
              setQuizSuccessVisible(false);
              if (targetEventId) {
                navigation.replace(SCREENS.BUY_TICKET as any, {eventId: targetEventId});
              } else {
                navigation.goBack();
              }
            }, 2200);
          } catch (e) {
            console.error('Error redirecting after quiz success', e);
            Alert.alert('Saved', 'Your answers have been saved.');
            navigation.goBack();
          }
        } else {
          Alert.alert('Saved', 'Your answers have been saved.');
          navigation.goBack();
        }
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    } catch (e: any) {
      console.log('api end error');
      Alert.alert('Save failed', e?.message || 'Could not save your answer. Please try again.');
    } finally {
      console.log('api end final');
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ModernHeader title="Quiz" onBackPress={goBackHandler} variant="elevated" />
      <ScrollView contentContainerStyle={styles.content}>
        <ModernCard variant="elevated" padding="xl" margin="sm" style={styles.card}>
          <View style={styles.heroRow}>
            <Text style={styles.title}>{titleText}</Text>
          </View>
          <Text style={styles.subtitle}>{subtitleText}</Text>
          <View style={styles.progressTrack}>
            <Animated.View
              style={[
                styles.progressFill,
                {
                  width: progressAnim.interpolate({inputRange: [0, 1], outputRange: ['0%', '100%']}),
                },
              ]}
            />
          </View>
        </ModernCard>

        {loading && (
          <ModernCard variant="default" padding="lg" margin="sm" style={styles.card}>
            <Text style={{color: colors.textSecondary}}>Loading...</Text>
          </ModernCard>
        )}

        {!!error && (
          <ModernCard variant="default" padding="lg" margin="sm" style={styles.card}>
            <Text style={{color: colors.error}}>Failed to load form status</Text>
          </ModernCard>
        )}

        {!loading && !error && hasNothingToFill && (
          <ModernCard variant="default" padding="lg" margin="sm" style={styles.card}>
            <Text style={{color: colors.textSecondary}}>You're all set. Nothing left to fill.</Text>
            <View style={{height: spacing.lg}} />
            <ModernButton title="Go back" onPress={() => navigation.goBack()} variant="primary" />
          </ModernCard>
        )}

        {!loading && !error && !hasNothingToFill && current && (
          <ModernCard variant="default" padding="lg" margin="sm" style={styles.card}>
            {/* Removed fraction text for a cleaner layout; progress bar above indicates progress */}
            <Text style={styles.questionTitle}>{current.label}</Text>
            {current.type === 'scale' && (
              <Text style={[styles.scaleInfo]}>Use the slider to choose a number from 0 to 10.</Text>
            )}
            {current.type === 'multiselect' && (
              <Text style={styles.subtitle}>
                {`Choose up to ${current.maxSelect || 3} option${(current.maxSelect || 3) > 1 ? 's' : ''}.`}
              </Text>
            )}

            {current.type === 'select' && (
              <View>
                {current.searchable && current.showSelectedAtTop && answers[current.key] && (
                  <View style={styles.selectedCountryContainer}>
                    <Text style={styles.selectedCountryText}>Selected: {answers[current.key]}</Text>
                  </View>
                )}
                
                {current.searchable && (
                  <TextInput
                    style={[styles.searchInput, mode === 'view' && {backgroundColor: colors.border + '10'}]}
                    placeholder="Search countries..."
                    placeholderTextColor={colors.textSecondary}
                    value={searchQuery}
                    onChangeText={text => {
                      if (mode !== 'view') {
                        setSearchQuery(text);
                        setShowSearchResults(text.length > 0);
                      }
                    }}
                    editable={mode !== 'view'}
                    onFocus={() => {
                      if (mode !== 'view') {
                        setShowSearchResults(true);
                      }
                    }}
                  />
                )}

                <View>
                  {(current.options || []).map(opt => {
                    const opts = current.options || [];
                    const currentVal = answers[current.key] || '';
                    const isOther = opt === 'Other';
                    const selectedBool = isOther
                      ? currentVal === 'Other' || (!!currentVal && !opts.includes(currentVal))
                      : currentVal === opt;
                    const showOtherInput = isOther && selectedBool;
                    const otherText =
                      showOtherInput && currentVal !== 'Other' && !opts.includes(currentVal) ? currentVal : '';

                    // Filter options if search is active
                    if (current.searchable && showSearchResults && searchQuery.length > 0) {
                      const searchLower = searchQuery.toLowerCase();
                      const countryName = opt.replace(/^[\u{1F1E6}-\u{1F1FF}]+\s*/u, '').toLowerCase();
                      if (!countryName.includes(searchLower) && !opt.toLowerCase().includes(searchLower)) {
                        return null;
                      }
                    }

                    // Show selected at top if enabled
                    if (current.showSelectedAtTop && !selectedBool && answers[current.key]) {
                      return null;
                    }

                    return (
                      <View key={opt}>
                        <TouchableOpacity
                          style={[
                            styles.option, 
                            selectedBool && styles.optionSelected,
                            mode === 'view' && {opacity: 0.7}
                          ]}
                          onPress={() => {
                            if (mode !== 'view') {
                              Haptic.trigger('selection');
                              if (isOther) {
                                // Selecting Other activates the input; set temp marker 'Other'.
                                setAnswers(prev => ({...prev, [current.key]: 'Other'}));
                              } else {
                                setAnswers(prev => ({...prev, [current.key]: opt}));
                              }
                              if (current.searchable) {
                                setSearchQuery('');
                                setShowSearchResults(false);
                              }
                            }
                          }}
                          activeOpacity={0.7}>
                          <View style={[styles.radioOuter, selectedBool && {borderColor: colors.primary}]}>
                            {selectedBool && <View style={styles.radioInner} />}
                          </View>
                          <Text style={styles.optionText}>{opt}</Text>
                        </TouchableOpacity>

                        {showOtherInput && (
                          <TextInput
                            style={[styles.input, {marginTop: 4}, mode === 'view' && {backgroundColor: colors.border + '10'}]}
                            placeholder="Please specify"
                            placeholderTextColor={colors.textSecondary}
                            value={otherText}
                            onChangeText={text => {
                              if (mode !== 'view') {
                                // Replace 'Other' marker with the user's custom text
                                setAnswers(prev => ({...prev, [current.key]: text}));
                              }
                            }}
                            editable={mode !== 'view'}
                            accessibilityLabel="Other option input"
                          />
                        )}
                      </View>
                    );
                  })}
                </View>
              </View>
            )}

            {(current.type === 'text' || current.type === 'multiline') && (
              <TextInput
                style={[
                  styles.input, 
                  current.type === 'multiline' && {height: 120, textAlignVertical: 'top'},
                  mode === 'view' && {backgroundColor: colors.border + '10'}
                ]}
                placeholder={current.placeholder || 'Type your answer'}
                placeholderTextColor={colors.textSecondary}
                keyboardType={'default'}
                value={answers[current.key] || ''}
                onChangeText={text => {
                  if (mode !== 'view') {
                    setAnswers(prev => ({...prev, [current.key]: text}));
                  }
                }}
                onFocus={() => {}}
                onBlur={() => {}}
                multiline={current.type === 'multiline'}
                editable={mode !== 'view'}
              />
            )}

            {current.type === 'date' && (
              <View>
                <TouchableOpacity
                  style={[
                    styles.inputButton,
                    mode === 'view' && {backgroundColor: colors.border + '10', opacity: 0.7}
                  ]}
                  onPress={() => {
                    if (mode !== 'view') {
                      triggerHaptic('selection');
                      // If there is an existing answer, use it as initial date
                      const existing = answers[current.key];
                      if (existing) {
                        const d = moment(existing, 'YYYY-MM-DDTHH:mm:ss', true).isValid()
                          ? moment(existing, 'YYYY-MM-DDTHH:mm:ss').toDate()
                          : new Date();
                        setTempDate(d);
                      } else {
                        setTempDate(new Date());
                      }
                      setIsDatePickerVisible(true);
                    }
                  }}
                  activeOpacity={0.7}
                  disabled={mode === 'view'}
                  accessibilityLabel="Open date picker">
                  <Text style={styles.inputButtonText}>
                    {(() => {
                      const v = answers[current.key];
                      if (!v) return current.placeholder || 'YYYY-MM-DD';
                      // Display date portion for clarity
                      const display = moment(v, 'YYYY-MM-DDTHH:mm:ss', true).isValid()
                        ? moment(v, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD')
                        : v.split('T')[0] || v;
                      return display;
                    })()}
                  </Text>
                </TouchableOpacity>

                <DateTimeModal
                  isVisible={isDatePickerVisible}
                  close={() => setIsDatePickerVisible(false)}
                  onPress={() => {
                    // Save in required format: YYYY-MM-DDTHH:mm:ss
                    const formatted = moment(tempDate).format('YYYY-MM-DDTHH:mm:ss');
                    setAnswers(prev => ({...prev, [current.key]: formatted}));
                    setIsDatePickerVisible(false);
                  }}
                  date={tempDate}
                  handleChooseMaxDate={(value?: Date) => {
                    if (value) setTempDate(value);
                  }}
                  maxAvailableDate={new Date()}
                  mode="date"
                />
              </View>
            )}

            {current.type === 'phone' && (
              <View style={styles.phoneRow}>
                <View style={styles.countryBox}>
                  <Text style={styles.countryText}>+44</Text>
                </View>
                <TextInput
                  style={[styles.phoneInput, mode === 'view' && {backgroundColor: colors.border + '10'}]}
                  placeholder={current.placeholder || 'Enter phone number'}
                  placeholderTextColor={colors.textSecondary}
                  keyboardType="phone-pad"
                  value={(() => {
                    const raw = answers[current.key] || '';
                    const prefix = '+44';
                    return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
                  })()}
                  onChangeText={text => {
                    if (mode !== 'view') {
                      const digits = text.replace(/[^0-9]/g, '');
                      setAnswers(prev => ({...prev, [current.key]: `+44${digits}`}));
                    }
                  }}
                  editable={mode !== 'view'}
                  maxLength={15}
                  accessibilityLabel="UK phone number"
                />
              </View>
            )}

            {current.type === 'scale' && (
              <View>
                <Text style={styles.sliderValue}>
                  {(() => {
                    const v = Number(answers[current.key]);
                    return Number.isFinite(v) ? v : 0;
                  })()}
                </Text>
                <View style={styles.sliderWrap}>
                  <Slider
                    minimumValue={0}
                    maximumValue={10}
                    step={1}
                    value={(() => {
                      const v = Number(answers[current.key]);
                      return Number.isFinite(v) ? v : 0; // default is 0
                    })()}
                    onValueChange={val => {
                      if (mode !== 'view') {
                        triggerHaptic('selection');
                        setAnswers(prev => ({...prev, [current.key]: String(Math.round(val))}));
                      }
                    }}
                    disabled={mode === 'view'}
                    minimumTrackTintColor={colors.primary}
                    maximumTrackTintColor={colors.border}
                    thumbTintColor={colors.primary}
                    accessibilityLabel="Scale selector"
                    accessibilityHint="Swipe or drag to set a number from 0 to 10"
                  />
                </View>
                <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
                  <Text style={{color: colors.textSecondary}}>0</Text>
                  <Text style={{color: colors.textSecondary}}>10</Text>
                </View>
              </View>
            )}

            {current.type === 'multiselect' && (
              <View>
                {(current.options || []).map(opt => {
                  const opts = current.options || [];
                  const list = (() => {
                    try {
                      const raw = answers[current.key];
                      const parsed = raw ? JSON.parse(raw) : [];
                      console.log(parsed, 'parsedparsed');
                      
                      return Array.isArray(parsed) ? (parsed as string[]) : [];
                    } catch {
                      return [] as string[];
                    }
                  })();

                  const customIndex = list.findIndex(v => !opts.includes(v));
                  const hasCustom = customIndex !== -1;
                  const isOther = opt === 'Other';
                  const isOtherSelected = list.includes('Other') || hasCustom;
                  const isSelected = isOther ? isOtherSelected : list.includes(opt);
                  const showOtherInput = isOther && isOtherSelected;
                  const otherText = hasCustom ? list[customIndex] : '';

                  const toggleStandard = () => {
                    if (mode === 'view') return;
                    triggerHaptic('selection');
                    const max = current.maxSelect ?? 3;
                    let next = list.slice();
                    const already = next.includes(opt);
                    if (already) {
                      next = next.filter(v => v !== opt);
                    } else {
                      if (next.length >= max) {
                        Alert.alert('Limit reached', `You can select up to ${max} options.`);
                        return;
                      }
                      next.push(opt);
                    }
                    setAnswers(prev => ({...prev, [current.key]: JSON.stringify(next)}));
                  };

                  const toggleOther = () => {
                    if (mode === 'view') return;
                    triggerHaptic('selection');
                    const max = current.maxSelect ?? 3;
                    // Remove both placeholder 'Other' and any existing custom when deselecting
                    let next = list.filter(v => (v === 'Other' ? false : opts.includes(v)));
                    if (isOtherSelected) {
                      // Deselect Other entirely
                      setAnswers(prev => ({...prev, [current.key]: JSON.stringify(next)}));
                      return;
                    }
                    if (next.length >= max) {
                      Alert.alert('Limit reached', `You can select up to ${max} options.`);
                      return;
                    }
                    next.push('Other');
                    setAnswers(prev => ({...prev, [current.key]: JSON.stringify(next)}));
                  };

                  return (
                    <View key={opt}>
                      <TouchableOpacity
                        style={[
                          styles.option, 
                          isSelected && styles.optionSelected,
                          mode === 'view' && {opacity: 0.7}
                        ]}
                        onPress={mode === 'view' ? undefined : (isOther ? toggleOther : toggleStandard)}
                        activeOpacity={0.7}
                        disabled={mode === 'view'}>
                        <View style={[styles.checkboxOuter, isSelected && {borderColor: colors.primary}]}>
                          {isSelected && <View style={styles.checkboxInner} />}
                        </View>
                        <Text style={styles.optionText}>{opt}</Text>
                      </TouchableOpacity>

                      {showOtherInput && (
                        <TextInput
                          style={[styles.input, {marginTop: 4}, mode === 'view' && {backgroundColor: colors.border + '10'}]}
                          placeholder="Please specify"
                          placeholderTextColor={colors.textSecondary}
                          value={otherText}
                          onChangeText={text => {
                            if (mode !== 'view') {
                              // Keep only standard options from the list, replace Other/custom with user's text
                              const kept = list.filter(v => opts.includes(v));
                              const trimmed = text.trim();
                              let next = kept.slice();
                              if (trimmed.length > 0) {
                                next.push(trimmed);
                              } else {
                                // If empty, keep placeholder 'Other' to indicate selection remains
                                next.push('Other');
                              }
                              setAnswers(prev => ({...prev, [current.key]: JSON.stringify(next)}));
                            }
                          }}
                          editable={mode !== 'view'}
                          accessibilityLabel="Other option input"
                        />
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </ModernCard>
        )}
      </ScrollView>

      {!loading && !error && !hasNothingToFill && current && (
        <View style={styles.footer}>
          <View style={styles.buttonRow}>
            <ModernButton
              title={currentIndex === 0 ? (mode === 'view' ? 'Close' : 'Cancel') : 'Back'}
              onPress={() => {
                if (currentIndex === 0) {
                  navigation.goBack();
                } else {
                  setCurrentIndex(i => Math.max(0, i - 1));
                }
              }}
              variant="outline"
              size="md"
              style={styles.buttonFlex}
            />

            {mode === 'view' ? (
              <ModernButton
                title={currentIndex === questions.length - 1 ? 'Done' : 'Next'}
                onPress={() => {
                  if (currentIndex === questions.length - 1) {
                    navigation.goBack();
                  } else {
                    setCurrentIndex(prev => prev + 1);
                  }
                }}
                variant="primary"
                size="md"
                style={styles.buttonFlex}
              />
            ) : (
              <ModernButton
                title={currentIndex === questions.length - 1 ? 'Finish' : 'Continue'}
                onPress={handleContinuePress}
                variant="primary"
                size="md"
                disabled={!isCurrentAnswered || submitting || isSaving}
                loading={submitting || isSaving}
                style={styles.buttonFlex}
              />
            )}
          </View>
        </View>
      )}
      <QuizSuccessModal
        visible={quizSuccessVisible}
        onClose={() => {
          setQuizSuccessVisible(false);
        }}
        formType={formType}
        lottieSource={successLottie}
      />
    </SafeAreaView>
  );
};

export default PyxiSelectQuiz;
